import type { CSSProperties, FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LogoMark } from '@/components/generals/logo'

import { mainLinks, productLinks } from '@/constants/site'

type HeaderTheme = 'light' | 'dark'

/**
 * Watches every section marked with data-header-theme and reports the one
 * sitting under the header, so the header can switch between its light and
 * dark glass. Sections without the attribute count as light.
 */
const useHeaderTheme = (path: string): HeaderTheme => {
    const [theme, setTheme] = useState<HeaderTheme>('light')

    useEffect(() => {
        let frame = 0

        const update = (): void => {
            frame = 0
            const x = Math.min(48, window.innerWidth / 2)
            const hit = document.elementsFromPoint(x, 40)
                .find((element) => !element.closest('header') && element.closest('[data-header-theme]'))
            const section = hit?.closest<HTMLElement>('[data-header-theme]')
            setTheme(section?.dataset.headerTheme === 'dark' ? 'dark' : 'light')
        }

        const schedule = (): void => {
            if (!frame) frame = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)

        return () => {
            if (frame) cancelAnimationFrame(frame)
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
        }
    }, [path])

    return theme
}

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const theme = useHeaderTheme(router.asPath)
    const [atTop, setAtTop] = useState(true)
    const [mobileOpen, setMobileOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)

    const closeMobile = useCallback((): void => setMobileOpen(false), [])
    const dark = theme === 'dark'

    useEffect(() => {
        let frame = 0

        const update = (): void => {
            frame = 0
            const next = window.scrollY < 24
            setAtTop((current) => (current === next ? current : next))
        }

        const onScroll = (): void => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', onScroll)
            if (frame) window.cancelAnimationFrame(frame)
        }
    }, [])

    useEffect(() => {
        router.events.on('routeChangeStart', closeMobile)
        router.events.on('hashChangeStart', closeMobile)

        return () => {
            router.events.off('routeChangeStart', closeMobile)
            router.events.off('hashChangeStart', closeMobile)
        }
    }, [router.events, closeMobile])

    useEffect(() => {
        if (!mobileOpen) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setMobileOpen(false)
        }

        const onPointerDown = (event: PointerEvent): void => {
            if (!menuRef.current?.contains(event.target as Node)) setMobileOpen(false)
        }

        document.addEventListener('keydown', onKeyDown)
        document.addEventListener('pointerdown', onPointerDown)

        return () => {
            document.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('pointerdown', onPointerDown)
        }
    }, [mobileOpen])

    /* Frosted glass in the page colour with a 5% ring and no drop shadow */
    const glass = dark ? 'glass-dusk' : 'glass-day'

    const linkClass = cn('nav-link h-10 px-3.5 text-[15px]', dark && 'nav-link-night')

    return (
        <header
            id='site-header'
            data-theme={theme}
            className='intro-drop fixed inset-x-0 top-0 z-50'
            style={{ '--intro-delay': '60ms' } as CSSProperties}
        >
            <div ref={menuRef} className='relative mx-auto flex w-full max-w-[90rem] items-start justify-between gap-3 px-3 pt-3 sm:px-5 sm:pt-3.5'>
                <Link
                    href='/'
                    aria-label='Forge home'
                    className={cn(
                        'flex h-[52px] items-center px-2 transition-colors duration-150 ease-out sm:h-14',
                        dark ? 'text-night-ink' : 'text-ink'
                    )}
                >
                    <LogoMark className='size-[26px] flex-none' />

                    <span
                        className={cn(
                            'overflow-hidden whitespace-nowrap text-[16px] font-medium uppercase leading-none tracking-[0.22em] transition-[max-width,opacity,margin] duration-500 ease-out',
                            atTop ? 'ml-3 max-w-32 opacity-100' : 'ml-0 max-w-0 opacity-0'
                        )}
                    >
                        Forge
                    </span>
                </Link>

                <div
                    className={cn(
                        'flex h-[52px] items-center gap-1 rounded-2xl p-1.5 transition-[background-color,box-shadow] duration-150 ease-out sm:h-14',
                        glass
                    )}
                >
                    <nav aria-label='Main' className='hidden items-center lg:flex'>
                        {mainLinks.map((link) => (
                            <Link key={link.href} href={link.href} className={linkClass}>
                                {link.label}
                            </Link>
                        ))}

                    </nav>

                    <Link
                        href='/demo'
                        className={cn(
                            'btn h-10 px-5 text-[15px] sm:h-11',
                            dark ? 'btn-night' : 'btn-primary'
                        )}
                    >
                        Book a demo
                    </Link>

                    <button
                        type='button'
                        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={mobileOpen}
                        aria-controls='mobile-menu'
                        onClick={() => setMobileOpen((current) => !current)}
                        className={cn(
                            'grid size-10 place-items-center rounded-full transition-colors duration-150 ease-out lg:hidden',
                            dark ? 'text-night-muted hover:bg-white/[0.08] hover:text-white' : 'text-ink-muted hover:bg-black/[0.05] hover:text-ink'
                        )}
                    >
                        <span className='stack-grid'>
                            <Menu className={cn('size-5 transition-[opacity,transform] duration-200', mobileOpen ? 'scale-75 opacity-0' : 'opacity-100')} aria-hidden='true' />
                            <X className={cn('size-5 transition-[opacity,transform] duration-200', mobileOpen ? 'opacity-100' : 'scale-75 opacity-0')} aria-hidden='true' />
                        </span>
                    </button>
                </div>

                <div
                    id='mobile-menu'
                    data-open={mobileOpen ? 'true' : 'false'}
                    inert={!mobileOpen}
                    className={cn(
                        'menu-panel absolute inset-x-3 top-full mt-2 origin-top rounded-xl p-2 sm:inset-x-5 lg:hidden',
                        dark && 'bg-[rgb(28_25_22/0.7)] text-night-ink shadow-[inset_0_0_0_1px_rgb(255_255_255/0.06)]'
                    )}
                >
                    <nav aria-label='Mobile' className='flex flex-col'>
                        {[...mainLinks, ...productLinks].map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                onClick={closeMobile}
                                className={cn(
                                    'flex items-center justify-between rounded-lg px-3 py-2.5 text-[15px] transition-colors duration-150 ease-out',
                                    dark ? 'text-night-muted hover:bg-white/[0.08] hover:text-white' : 'text-ink-muted hover:bg-black/[0.05] hover:text-ink'
                                )}
                            >
                                {link.label}
                                <ArrowRight className='size-4 opacity-40' aria-hidden='true' />
                            </Link>
                        ))}
                    </nav>
                </div>
            </div>
        </header>
    )
}
